// src/components/scratch/ResponsibleGamblingDisclaimer.jsx
import React from "react";
import Card from "../ui/Card";
import Icon from "../ui/Icon";

/**
 * Responsible-gambling + methodology disclaimer.
 *
 * Reused across every scratch page. The copy mirrors the voice already
 * established in Legal.jsx (entertainment-only, 18+, no guaranteed winnings,
 * spend only what you can afford) and is deliberately explicit that the
 * Scratch Score ranks the *value of remaining prizes from public data* — it
 * does NOT predict winners or improve anyone's odds.
 *
 * Variants:
 *   - "full"    a bordered card with the full statement (page footer)
 *   - "compact" a single muted line (under a chart/score)
 *   - "inline"  one short sentence (badge-adjacent)
 */
export default function ResponsibleGamblingDisclaimer({ variant = "full", className = "" }) {
  if (variant === "inline") {
    return (
      <p className={`text-[11px] ${className}`} style={{ color: "var(--txt-3)" }}>
        Based on public prize data. No guarantee of winning.
      </p>
    );
  }

  if (variant === "compact") {
    return (
      <p
        className={`text-[12px] leading-relaxed ${className}`}
        style={{ color: "var(--txt-3)" }}
      >
        The Scratch Score ranks the strength of <em>remaining</em> prizes using
        publicly published lottery data. It is an entertainment tool, not a
        prediction of winners and not a way to improve your odds.
      </p>
    );
  }

  return (
    <Card variant="soft" className={className}>
      <div className="flex items-start gap-3">
        <span
          className="inline-flex items-center justify-center rounded-full shrink-0"
          style={{
            width: 34,
            height: 34,
            background: "var(--surf-3)",
            color: "var(--txt-2)",
          }}
        >
          <Icon name="shield-check" size={18} />
        </span>
        <div className="space-y-2">
          <h4 className="font-semibold text-[13px]" style={{ color: "var(--txt)" }}>
            How to read these insights
          </h4>
          <p className="text-[12px] leading-relaxed" style={{ color: "var(--txt-2)" }}>
            LottoGPT analyzes <strong>public prize-inventory data</strong> published by
            state lotteries to highlight games with{" "}
            <strong>higher remaining prize strength</strong> and{" "}
            <strong>better relative value</strong>. This is for{" "}
            <strong>entertainment purposes only</strong>.
          </p>
          <ul
            className="text-[12px] leading-relaxed space-y-1 list-disc pl-4"
            style={{ color: "var(--txt-2)" }}
          >
            <li>
              We do <strong>not predict winning tickets</strong> and cannot
              improve your odds — scratch-off outcomes are random.
            </li>
            <li>
              There is <strong>no guarantee of winning</strong>, and past prize
              data does not affect any future result.
            </li>
            <li>
              You must be <strong>18 or older</strong> (or the legal age in your
              jurisdiction) to play, and should only spend what you can
              comfortably afford to lose.
            </li>
          </ul>
          <p className="text-[11px]" style={{ color: "var(--txt-3)" }}>
            If gambling stops being fun, free, confidential help is available at{" "}
            <a
              href="https://www.1800gambler.net"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--gold)" }}
            >
              1-800-GAMBLER
            </a>
            .
          </p>
        </div>
      </div>
    </Card>
  );
}
